import CheckCircle from '@betfan/icons/duotone/EssentionalUI/CheckCircle.svg?react';
import DangerCircle from '@betfan/icons/duotone/EssentionalUI/DangerCircle.svg?react';
import DangerTriangle from '@betfan/icons/duotone/EssentionalUI/DangerTriangle.svg?react';
import InfoCircle from '@betfan/icons/duotone/EssentionalUI/InfoCircle.svg?react';
import Close from '@betfan/icons/linear/EssentionalUI/Close.svg?react';
import cn from 'classnames';
import React, { ComponentType, PropsWithChildren, ReactElement } from 'react';
import { DSSpinner } from './animations';
import { DSButton, DSButtonColor } from './DSButton';

/**
 * Cannot partially fill in the properties of T. Either all of them are present or none of them.
 */
type AllOrNone<T> = T | { [K in keyof T]?: never };

type DSModalType = 'success' | 'info' | 'warning' | 'error' | 'spinner';

// prettier-ignore
type DSModalProps =
    & Pick<React.HTMLAttributes<HTMLDivElement>, 'className'>
    & { title: string; type?: DSModalType; }
    & (
      /**
       * Default footer look. Either:
       *  - both confirm and cancel buttons
       *  - only confirm button
       *  - only cancel button
       *  - no buttons
       *
       * showCloseIcon may only be specified when we're providing the cancelButtonAction
       */
		  | (
          // Confirm button properties - either all or none
          & AllOrNone<{ confirmButtonText?: string; confirmButtonAction: () => void }>
          // Cancel button properties - either all or none
          & AllOrNone<{ cancelButtonText?: string; cancelButtonAction: () => void; showCloseIcon?: boolean }>
          // Additional footer button config
          & { horizontalButtons?: boolean; footer?: never; alignment?: 'left' | 'center'}
      )

      /**
       * Custom footer look
       *
       * Here no other footer related properties may be specified.
       */
      | {
          footer?: ComponentType<any> | ReactElement;
          confirmButtonText?: never;
          confirmButtonAction?: never;
          cancelButtonText?: never;
          cancelButtonAction?: never;
          showCloseIcon?: never;
          horizontalButtons?: never;
		  alignment?: never,
      }
    );

export const DSModal = ({
	type,
	horizontalButtons,
	showCloseIcon,
	cancelButtonAction,
	title,
	children,
	footer,
	confirmButtonText,
	cancelButtonText,
	confirmButtonAction,
	alignment = 'center',
}: PropsWithChildren<DSModalProps>) => {
	const iconColor = () => {
		switch (type) {
			case 'success':
				return 'text-success-700 duotone:text-success-300 dark:text-success-500 dark:duotone:text-success-700';
			case 'info':
				return 'text-info-700 duotone:text-info-300 dark:text-info-500 dark:duotone:text-info-700';
			case 'warning':
				return 'text-warning-700 duotone:text-warning-300 dark:text-warning-500 dark:duotone:text-warning-700';
			case 'error':
				return 'text-error-700 duotone:text-error-300 dark:text-error-500 dark:duotone:text-error-700';
			case 'spinner':
				return 'text-primary-500';
			default:
				return '';
		}
	};

	const iconClasses = () => cn(iconColor(), 'duotone:opacity-100', type === 'spinner' ? 'w-16 h-16' : 'w-12 h-12');

	const iconContainer = () => {
		if (!type) {
			return undefined;
		}

		return <div className={cn('flex', alignment === 'left' ? 'justify-start' : 'justify-center')}>{icon()}</div>;
	};

	const icon = () => {
		if (!type) {
			return undefined;
		}

		switch (type) {
			case 'success':
				return <CheckCircle className={iconClasses()} />;
			case 'info':
				return <InfoCircle className={iconClasses()} />;
			case 'warning':
				return <DangerTriangle className={iconClasses()} />;
			case 'error':
				return <DangerCircle className={iconClasses()} />;
			case 'spinner':
				return <DSSpinner className={iconClasses()} />;
		}
	};

	const classes = () =>
		cn(
			'rounded-16 w-full max-w-modal-sm max-h-[calc(100vh-2rem)] overflow-auto m-4',
			'bg-white dark:bg-dark-200 text-gray-700 dark:text-gray-100',
			type === 'spinner' ? 'py-8 px-3' : 'p-3',
		);

	const titleClasses = () => cn('text-center text-h3 font-bold text-gray-900 dark:text-gray-100');

	const contentClasses = () => cn('text-gray-700 dark:text-gray-200', alignment === 'left' ? 'text-left' : 'text-center');

	const footerClasses = () => cn('flex gap-2 mt-6', horizontalButtons ? 'flex-row-reverse' : 'flex-col');

	const closeIconClasses = () => cn('w-6 h-6 text-gray-900 dark:text-gray-300');

	const confirmButtonColor = (): DSButtonColor => {
		switch (type) {
			case 'error':
				return 'error';
			default:
				return 'primary';
		}
	};

	const wrapperClasses = () => cn('flex flex-col gap-1 mt-3', alignment === 'left' ? 'items-start w-full' : 'items-center');

	return (
		<div className={classes()}>
			{showCloseIcon && (
				<div className="flex justify-end">
					<button onClick={cancelButtonAction}>
						<Close className={closeIconClasses()} />
					</button>
				</div>
			)}
			{iconContainer()}
			<div className={wrapperClasses()}>
				<div className={titleClasses()}>{title}</div>
				<div className={contentClasses()}>{children}</div>
			</div>

			{footer ? (
				<div className="mt-6">{footer}</div>
			) : (
				(confirmButtonText || cancelButtonText) && (
					<div className={footerClasses()}>
						{confirmButtonText && (
							<DSButton type="submit" color={confirmButtonColor()} size="medium" className="w-full" onClick={confirmButtonAction}>
								{confirmButtonText}
							</DSButton>
						)}
						{cancelButtonText && (
							<DSButton color="tertiary" size="medium" className="w-full" onClick={cancelButtonAction}>
								{cancelButtonText}
							</DSButton>
						)}
					</div>
				)
			)}
		</div>
	);
};
